import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  PageContainer,
  PageToolbar,
  SectionTitle,
  SectionSubtitle,
  EmptyState,
  ToolbarDivider,
} from '../../components/ui/PageLayout';
import {
  AnimatedTabIndicator,
  animatedTabTriggerClass,
} from '../../components/ui/AnimatedTabIndicator';
import { streamsAPI } from '../../services/api';
import toast from '../../services/toast';
import { useConfirm } from '../../contexts/ConfirmContext';
import AssignStreamMemberModal from './AssignStreamMemberModal';
import AssignProjectMemberModal from '../ProjectDetail/AssignProjectMemberModal';
import ContributorsBarChartCard from '../../components/contributors/ContributorsBarChartCard';
import { Waves, Plus, FolderKanban, Trash2, GitFork, Users, BarChart3 } from 'lucide-react';
import { PageToolbarSkeleton, CardGridSkeleton } from '../../components/ui/Skeleton';
import { FeatureGate, useFeatureFlag } from '../../contexts/FeatureFlagContext';
import { useOrganisationName } from '../../hooks/useOrganisationName';
import { useResolvedOrgId } from '../../hooks/useResolvedOrgId';
import { streamPageKeys, invalidateStreamListAndPage } from '../../query/queryKeys';
import { useStreamsPageQueries } from './useStreamsPageQueries';
import { useStreamRepositoriesAggregate } from './useStreamRepositoriesAggregate';
import StreamDetailOverviewTab from './StreamDetailOverviewTab';
import StreamsOverviewPanel from './StreamsOverviewPanel';
import StreamsRepositoriesTab from './StreamsRepositoriesTab';
import { ORGANISATION_STREAMS_CHANGED } from '../../constants/orgDashboardEvents';
import OrganisationStreamCard from '../../components/streams/OrganisationStreamCard';
import OrganisationProjectCard from '../../components/projects/OrganisationProjectCard';
import { OrgMemberAvatarWithCard } from '../../components/ui/OrgMemberAvatar';
import {
  teamSectionHeaderClass,
  teamMemberRowClass,
  memberNameClass,
  memberRoleClass,
} from '../../components/members/memberViewTheme';

const Streams = () => {
  const { streamId } = useParams();
  const queryClient = useQueryClient();
  const { confirm } = useConfirm();
  const orgName = useOrganisationName();
  const { orgId, ready } = useResolvedOrgId();

  const [assignMemberStreamId, setAssignMemberStreamId] = useState<any>(null);
  const [assignMemberProject, setAssignMemberProject] = useState<any>(null);
  const [deploymentPeriod, setDeploymentPeriod] = useState('yearly');
  const [leadTimePeriod, setLeadTimePeriod] = useState('yearly');
  const [doraChartType, setDoraChartType] = useState('area');
  const [leadTimeChartType, setLeadTimeChartType] = useState('area');
  const [churnPeriod, setChurnPeriod] = useState('yearly');
  const [churnChartType, setChurnChartType] = useState('area');
  const [contributorsTimeframe, setContributorsTimeframe] = useState('week');
  const [contributorsStatus, setContributorsStatus] = useState('active');
  const [activeStreamTab, setActiveStreamTab] = useState('overview');
  const showStreamMembersTab = useFeatureFlag('ui.streams.membersTab', false);

  const deploymentWindowDays =
    deploymentPeriod === 'weekly' ? 7 : deploymentPeriod === 'yearly' ? 365 : 30;
  const leadTimeWindowDays =
    leadTimePeriod === 'weekly' ? 7 : leadTimePeriod === 'yearly' ? 365 : 30;
  const churnWindowDays = churnPeriod === 'weekly' ? 7 : churnPeriod === 'yearly' ? 365 : 30;

  const {
    streamsQuery,
    streams,
    projects,
    streamMembers,
    streamContributorRows,
    streamContributorsLoading,
    deploymentDora,
    deploymentDoraMeta,
    deploymentLoading,
    leadTimeDora,
    leadTimeDoraMeta,
    leadTimeLoading,
    churnData,
    churnLoading,
  } = useStreamsPageQueries({
    orgId,
    streamId,
    deploymentWindowDays,
    leadTimeWindowDays,
    churnWindowDays,
    contributorsTimeframe,
    contributorsStatus,
  });

  const { streamReposFlat, streamReposLoading } = useStreamRepositoriesAggregate({
    activeStreamTab,
    orgId,
    streamId,
    projects,
  });

  const loading = !ready || (!!orgId && streamsQuery.isPending);

  useEffect(() => {
    const onChanged = () => {
      if (orgId) invalidateStreamListAndPage(queryClient, orgId, streamId);
    };
    window.addEventListener(ORGANISATION_STREAMS_CHANGED, onChanged);
    return () => window.removeEventListener(ORGANISATION_STREAMS_CHANGED, onChanged);
  }, [orgId, streamId, queryClient]);

  useEffect(() => {
    setActiveStreamTab('overview');
  }, [streamId]);

  useEffect(() => {
    if (!showStreamMembersTab && activeStreamTab === 'members') {
      setActiveStreamTab('overview');
    }
  }, [showStreamMembersTab, activeStreamTab]);

  const streamDetailTabs = useMemo(() => {
    const tabs = [
      { id: 'overview', label: 'Overview', icon: BarChart3 },
      { id: 'metrics', label: 'Metrics', icon: BarChart3 },
      { id: 'projects', label: 'Projects', icon: FolderKanban },
      { id: 'repositories', label: 'Repositories', icon: GitFork },
    ];
    if (showStreamMembersTab) {
      tabs.push({ id: 'members', label: 'Members', icon: Users });
    }
    return tabs;
  }, [showStreamMembersTab]);

  const onStreamTabListKeyDown = useCallback(
    (e) => {
      const ids = streamDetailTabs.map((t) => t.id);
      const idx = ids.indexOf(activeStreamTab);
      if (idx < 0) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setActiveStreamTab(ids[(idx + 1) % ids.length]);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setActiveStreamTab(ids[(idx - 1 + ids.length) % ids.length]);
      } else if (e.key === 'Home') {
        e.preventDefault();
        setActiveStreamTab(ids[0]);
      } else if (e.key === 'End') {
        e.preventDefault();
        setActiveStreamTab(ids[ids.length - 1]);
      }
    },
    [streamDetailTabs, activeStreamTab]
  );

  const refetchStreams = useCallback(() => {
    if (orgId) invalidateStreamListAndPage(queryClient, orgId, undefined);
  }, [queryClient, orgId]);

  const refetchStreamMembers = useCallback(() => {
    if (orgId && streamId) {
      queryClient.invalidateQueries({ queryKey: streamPageKeys.members(orgId, streamId) });
    }
  }, [queryClient, orgId, streamId]);

  if (loading) {
    return (
      <PageContainer>
        <PageToolbarSkeleton />
        <div className="border-t border-dark-border my-4" />
        <CardGridSkeleton count={6} />
      </PageContainer>
    );
  }

  if (!orgId) {
    return (
      <PageContainer>
        <PageToolbar title="Streams" />
        <p className="text-slate-400">No organisation found. Please complete setup.</p>
      </PageContainer>
    );
  }

  const selectedStream = streamId ? streams.find((s) => s.id === streamId) : null;

  const handleRemoveStreamMember = async (userId) => {
    if (!orgId || !streamId) return;
    const member = streamMembers.find((m) => (m.userId || m.id) === userId);
    const name = member?.username || member?.email || 'this member';
    const ok = await confirm({
      title: 'Remove member from stream',
      message: `Remove ${name} from this stream?`,
      confirmLabel: 'Remove',
    });
    if (!ok) return;
    try {
      await streamsAPI.removeMember(orgId, streamId, userId);
      refetchStreamMembers();
      refetchStreams();
      toast.success('Member removed from stream');
    } catch (err) {
      toast.error((err as Error).message || 'Failed to remove member');
    }
  };

  if (streamId && selectedStream) {
    const orgLabel = orgName || 'Organisation';

    return (
      <PageContainer>
        <PageToolbar
          title={selectedStream.name}
          breadcrumbItems={[{ label: orgLabel, to: '/' }, { label: selectedStream.name }]}
        >
          <button
            type="button"
            onClick={() => setAssignMemberStreamId(streamId)}
            className="btn-icon-mdl btn-icon-mdl-sm"
            aria-label="Add member to stream"
            title="Add member to stream"
          >
            <Users className="w-5 h-5" />
          </button>
        </PageToolbar>
        <ToolbarDivider />

        <div className="border-b border-dark-border mb-6">
          <nav
            className="flex gap-1 -mb-px overflow-x-auto"
            role="tablist"
            aria-label="Stream sections"
            onKeyDown={onStreamTabListKeyDown}
          >
            {streamDetailTabs.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                role="tab"
                id={`stream-tab-${id}`}
                aria-selected={activeStreamTab === id}
                aria-controls={`stream-tabpanel-${id}`}
                tabIndex={activeStreamTab === id ? 0 : -1}
                onClick={() => setActiveStreamTab(id)}
                className={animatedTabTriggerClass(activeStreamTab === id)}
              >
                <AnimatedTabIndicator
                  layoutId="stream-detail-tab-indicator"
                  isActive={activeStreamTab === id}
                />
                <span className="relative z-10 inline-flex items-center gap-2">
                  <Icon className="w-4 h-4" aria-hidden />
                  {label}
                </span>
              </button>
            ))}
          </nav>
        </div>

        {activeStreamTab === 'overview' && (
          <StreamDetailOverviewTab
            orgId={orgId}
            streamId={streamId}
            projects={projects}
            streamReposFlat={streamReposFlat}
            streamReposLoading={streamReposLoading}
            deploymentLoading={deploymentLoading}
            deploymentDora={deploymentDora}
            deploymentWindowDays={deploymentWindowDays}
            leadTimeLoading={leadTimeLoading}
            leadTimeDoraMeta={leadTimeDoraMeta}
            activeStreamTab={activeStreamTab}
            onGoToMetrics={() => setActiveStreamTab('metrics')}
            onGoToProjects={() => setActiveStreamTab('projects')}
          />
        )}

        {activeStreamTab === 'metrics' && (
          <div role="tabpanel" id="stream-tabpanel-metrics" aria-labelledby="stream-tab-metrics">
            <StreamsOverviewPanel
              deploymentLoading={deploymentLoading}
              deploymentDora={deploymentDora}
              deploymentDoraMeta={deploymentDoraMeta}
              deploymentWindowDays={deploymentWindowDays}
              deploymentPeriod={deploymentPeriod}
              doraChartType={doraChartType}
              setDeploymentPeriod={setDeploymentPeriod}
              setDoraChartType={setDoraChartType}
              leadTimeLoading={leadTimeLoading}
              leadTimeDora={leadTimeDora}
              leadTimeDoraMeta={leadTimeDoraMeta}
              leadTimePeriod={leadTimePeriod}
              leadTimeChartType={leadTimeChartType}
              setLeadTimePeriod={setLeadTimePeriod}
              setLeadTimeChartType={setLeadTimeChartType}
              churnLoading={churnLoading}
              churnData={churnData}
              churnWindowDays={churnWindowDays}
              churnPeriod={churnPeriod}
              churnChartType={churnChartType}
              setChurnPeriod={setChurnPeriod}
              setChurnChartType={setChurnChartType}
              metricsFetchScope={orgId && streamId ? { orgId, streamId } : undefined}
            />
          </div>
        )}

        {activeStreamTab === 'projects' && (
          <div role="tabpanel" id="stream-tabpanel-projects" aria-labelledby="stream-tab-projects">
            <div className="section-block-mdl">
              <SectionTitle className="mb-4">Projects</SectionTitle>
              {projects.length === 0 ? (
                <p className="text-slate-400">No projects in this stream yet.</p>
              ) : (
                <div className="cards-grid-mdl-dense">
                  {projects.map((p) => (
                    <OrganisationProjectCard
                      key={p.id}
                      orgId={orgId}
                      streamId={streamId}
                      project={p}
                      streamLabel={selectedStream.name}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeStreamTab === 'repositories' && (
          <StreamsRepositoriesTab
            projects={projects}
            streamReposLoading={streamReposLoading}
            streamReposFlat={streamReposFlat}
          />
        )}

        {activeStreamTab === 'members' && showStreamMembersTab && (
          <div role="tabpanel" id="stream-tabpanel-members" aria-labelledby="stream-tab-members">
            <div className="section-block-mdl">
              <div className={`${teamSectionHeaderClass} mb-0 rounded-t-xl`}>
                <SectionSubtitle className="text-slate-200">Stream members</SectionSubtitle>
                <button
                  type="button"
                  onClick={() => setAssignMemberStreamId(streamId)}
                  className="btn-icon-mdl btn-icon-mdl-sm"
                  aria-label="Add member to stream"
                  title="Add member to stream"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              {streamMembers.length === 0 ? (
                <p className="px-4 py-6 text-slate-400">No members assigned to this stream.</p>
              ) : (
                <ul className="card-mdl-static overflow-hidden rounded-b-xl rounded-t-none border-t-0">
                  {streamMembers.map((m) => {
                    const uid = m.userId || m.id;
                    return (
                      <li key={uid} className={teamMemberRowClass}>
                        <span className={`flex min-w-0 items-center gap-2 ${memberNameClass}`}>
                          <OrgMemberAvatarWithCard member={m} size="sm" />
                          <span className="min-w-0 truncate">
                            {m.username}
                            {m.email && (
                              <span className={`${memberRoleClass} ml-1`}>({m.email})</span>
                            )}
                          </span>
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveStreamMember(uid)}
                          className="btn-icon-mdl btn-icon-mdl-sm text-slate-400 hover:text-red-600 hover:bg-red-900/20"
                          aria-label={`Remove ${m.username || m.email}`}
                          title="Remove from stream"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <FeatureGate flag="ui.streams.contributorsBarChart">
              <div className="section-block-mdl">
                <ContributorsBarChartCard
                  contributorRows={streamContributorRows}
                  loading={streamContributorsLoading}
                  listKey={`stream-${streamId}`}
                  emptyMessage="No contributor data in this stream yet. Sync repositories to load commits."
                  timeframe={contributorsTimeframe}
                  status={contributorsStatus}
                  onTimeframeChange={setContributorsTimeframe}
                  onStatusChange={setContributorsStatus}
                />
              </div>
            </FeatureGate>
          </div>
        )}

        <AssignStreamMemberModal
          isOpen={!!assignMemberStreamId}
          onClose={() => setAssignMemberStreamId(null)}
          orgId={orgId}
          streamId={assignMemberStreamId}
          streamName={
            assignMemberStreamId
              ? (streams.find((s) => s.id === assignMemberStreamId)?.name ?? selectedStream?.name)
              : null
          }
          onSuccess={() => {
            refetchStreamMembers();
            refetchStreams();
          }}
        />
        <AssignProjectMemberModal
          isOpen={!!assignMemberProject}
          onClose={() => setAssignMemberProject(null)}
          orgId={orgId}
          streamId={assignMemberProject ? streamId : null}
          projectId={assignMemberProject?.id}
          projectName={assignMemberProject?.name}
          onSuccess={() => {
            if (orgId && streamId) {
              queryClient.invalidateQueries({ queryKey: streamPageKeys.projects(orgId, streamId) });
            }
          }}
        />
      </PageContainer>
    );
  }

  const orgLabel = orgName || 'Organisation';
  return (
    <PageContainer>
      <PageToolbar
        title="Streams"
        breadcrumbItems={[{ label: orgLabel, to: '/' }, { label: 'Streams' }]}
      />
      <ToolbarDivider />

      {streams.length === 0 ? (
        <EmptyState
          icon={<Waves className="w-12 h-12 text-slate-500" />}
          title="No streams yet."
          description="Add streams from Configuration → System."
        />
      ) : (
        <div className="cards-grid-mdl-dense">
          {streams.map((s) => (
            <OrganisationStreamCard key={s.id} orgId={orgId} stream={s} />
          ))}
        </div>
      )}

      <AssignStreamMemberModal
        isOpen={!!assignMemberStreamId}
        onClose={() => setAssignMemberStreamId(null)}
        orgId={orgId}
        streamId={assignMemberStreamId}
        streamName={
          assignMemberStreamId ? streams.find((s) => s.id === assignMemberStreamId)?.name : null
        }
        onSuccess={refetchStreams}
      />
    </PageContainer>
  );
};

export default Streams;
